import { useSetSelectedPerson } from "@shared/client";
import { useCallback, useEffect, useState } from "react";
import { type CountPersonProps } from "../types/countPerson.type";

export const useCountPerson = ({ min, max, adult, child, infant, ...rest }: CountPersonProps) => {
    const { isChoiceRoom, initialOnPrice, initialOffPrice, setNewOnPrice, setNewOffPrice } = rest;

    const [adultState, setAdultState] = useState(Number(min));
    const [adultUseState, setAdultUseState] = useState(0);
    const [childState, setChildState] = useState(0);
    const [infantState, setInfantState] = useState(0);
    const [totalAddFee, setTotalAddFee] = useState(0);
    const setSelectedPerson = useSetSelectedPerson();

    const totalCount = adultState + childState + infantState;
    const personCountMap: Record<0 | 1 | 2, number> = {
        0: adultState,
        1: childState,
        2: infantState,
    };

    const updateState = (
        setState: React.Dispatch<React.SetStateAction<number>>,
        increment: boolean,
        limit: number,
        minLimit: number = 0,
    ) => {
        setState((prev) => {
            if (increment) {
                return prev < limit ? prev + 1 : prev;
            }
            return prev > minLimit ? prev - 1 : prev;
        });
    };

    const handleCountPerson = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>, idx: number, currentCount: number) => {
            const isIncrement = e.currentTarget?.textContent === "+";
            if (!isIncrement && currentCount === 0) return;
            if (isIncrement && totalCount >= max) return;

            switch (idx) {
                case 0:
                    updateState(setAdultState, isIncrement, max, Number(min));
                    updateState(setAdultUseState, isIncrement, max);
                    break;
                case 1:
                    updateState(setChildState, isIncrement, max);
                    break;
                case 2:
                    updateState(setInfantState, isIncrement, max);
                    break;
                default:
                    break;
            }
        },
        [totalCount, max, min],
    );

    useEffect(() => {
        if (!isChoiceRoom) {
            setAdultState(Number(min));
            setAdultUseState(0);
            setChildState(0);
            setInfantState(0);
        }
    }, [isChoiceRoom]);

    useEffect(() => {
        const adultFee = adultUseState * adult;
        const childFee = childState * child;
        const infantFee = infantState * infant;
        setTotalAddFee(adultFee + childFee + infantFee);

        const person = {
            adult: adultUseState,
            child: childState,
            infant: infantState,
        };
        setSelectedPerson(person);
    }, [adultState, adultUseState, childState, infantState]);

    useEffect(() => {
        setNewOnPrice(initialOnPrice + totalAddFee);
        setNewOffPrice(initialOffPrice + totalAddFee);
    }, [totalAddFee]);

    return { handleCountPerson, personCountMap, totalCount };
};
